// DataTable — the generic List-screen table (Frontend Rules §8). Column-config
// driven, controlled sort, loading skeleton + empty state built in, memoized
// rows (Rule 6/11), and a mobile card-collapse so it stays usable at 375px
// (Rule 12: tables collapse to card view on mobile). Sorting is controlled by
// the parent so it can be server-driven; pass a comparator-free `sort` and
// pre-sorted `rows` for server mode, or use the built-in client sort helper.
import { memo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { cx } from '../../../utils/cx';
import { SortHeader } from './SortHeader';
import { Skeleton } from '../feedback/Skeleton';
import { EmptyState } from '../feedback/EmptyState';
import './data-table.css';

const TableRow = memo(function TableRow({ row, columns, rowClassName, onRowClick }) {
  return (
    <tr
      className={cx('data-table__row', onRowClick && 'is-clickable', rowClassName && rowClassName(row))}
      onClick={onRowClick ? () => onRowClick(row) : undefined}
    >
      {columns.map((col) => (
        <td
          key={col.key}
          className={cx('data-table__td', col.numeric && 'is-numeric', `is-${col.align || 'left'}`)}
          data-label={typeof col.label === 'string' ? col.label : undefined}
        >
          {col.render ? col.render(row[col.key], row) : row[col.key]}
        </td>
      ))}
    </tr>
  );
});
TableRow.propTypes = {
  row: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  rowClassName: PropTypes.func,
  onRowClick: PropTypes.func,
};

export function DataTable({
  columns,
  rows,
  sort,
  onSort,
  rowKey = (r, i) => r.id ?? i,
  rowClassName,
  loading = false,
  empty,
  onRowClick,
  className,
}) {
  const { t } = useTranslation();
  if (loading) {
    return (
      <div className={cx('data-table', className)}>
        <Skeleton variant="text" count={6} />
      </div>
    );
  }

  if (!rows || rows.length === 0) {
    return empty || (
      <EmptyState
        title={t('reusable.dataTable.emptyTitle')}
        message={t('reusable.dataTable.emptyMessage')}
      />
    );
  }

  return (
    <div className={cx('data-table', className)} role="region" aria-label={t('reusable.dataTable.ariaLabel')}>
      <table className="data-table__table">
        <thead>
          <tr>
            {columns.map((col) => (
              <SortHeader key={col.key} column={col} sort={sort} onSort={onSort} />
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <TableRow
              key={rowKey(row, i)}
              row={row}
              columns={columns}
              rowClassName={rowClassName}
              onRowClick={onRowClick}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

DataTable.propTypes = {
  /** [{ key, label, align?, numeric?, sortable?, render?(value,row) }] */
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node,
      align: PropTypes.oneOf(['left', 'center', 'right']),
      numeric: PropTypes.bool,
      sortable: PropTypes.bool,
      render: PropTypes.func,
    }),
  ).isRequired,
  rows: PropTypes.array,
  sort: PropTypes.shape({ key: PropTypes.string, dir: PropTypes.oneOf(['asc', 'desc']) }),
  onSort: PropTypes.func,
  /** Stable key for a row; defaults to row.id then index. */
  rowKey: PropTypes.func,
  /** Optional (row) => className for per-row emphasis (e.g. highlight held rows). */
  rowClassName: PropTypes.func,
  loading: PropTypes.bool,
  /** Custom empty node; defaults to a standard EmptyState. */
  empty: PropTypes.node,
  onRowClick: PropTypes.func,
  className: PropTypes.string,
};

// Client-side sort helper for in-memory datasets (<100 rows; virtualize above).
export const sortRows = (rows, sort) => {
  if (!sort?.key) return rows;
  const dir = sort.dir === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const av = a[sort.key];
    const bv = b[sort.key];
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
    return String(av).localeCompare(String(bv)) * dir;
  });
};

export default DataTable;
